/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { DropdownSelect } from 'components/generic/dropdown/DropdownSelect'
import { pluralize } from 'helpers/formatters'
import {
  updateSelectedAgentsMultiple,
  initializeAgents,
  updateAgentMatches,
  initializeSelectedAgents,
} from 'reducers/agentTypeahead'
import { AsyncSubscribe } from 'asyncHandlers/AsyncBase'
import s from './SalesAgent.module.css'

export function SalesAgentComponent({
  agents,
  selectedAgents,
  updateSelectedAgentsMultiple,
  initializeAgents,
  updateAgentMatches,
  agentType,
  initialSelectedAgents,
  matrixContext,
}) {
  useEffect(() => {
    if (agents.length > 0) {
      initializeAgents(agents)
      updateAgentMatches()
    }
  }, [agents, initializeAgents, updateAgentMatches])

  useEffect(() => {
    if (initialSelectedAgents.length > 0) {
      updateSelectedAgentsMultiple(initialSelectedAgents.map((agent) => Number(agent)))
    }
  }, [initialSelectedAgents, agents, updateSelectedAgentsMultiple])

  const options = agents?.map((agent) => ({
    label: (
      <div className={s.agentLabel}>
        {
          <div className={s.agentThumb}>
            {agent.thumbUrl ? (
              <img src={agent.thumbUrl} alt={agent.name} className={s.agentImage} />
            ) : (
              <div className={s.noImage} />
            )}
          </div>
        }{' '}
        {agent.name}
      </div>
    ),
    name: agent.name,
    value: agent.id || agent.value,
  }))

  const selectedValues = useMemo(() => {
    return selectedAgents?.map((agent) => agent.id || agent.value)
  }, [selectedAgents])

  const handleApply = (values) => {
    updateSelectedAgentsMultiple(values)
  }

  const handleClear = () => {
    updateSelectedAgentsMultiple([])
  }

  const getPlaceholderText = () => {
    switch (agentType) {
      case 'salesAgent':
        return 'Search Sales Agents'
      case 'listingAgent':
        return 'Search Listing Agents'
      case 'propertyOwner':
        return 'Search Property Owners'
      default:
        return 'Search Agents'
    }
  }

  const getSelectedValuesText = (valuesNum) => {
    switch (agentType) {
      case 'salesAgent':
        return valuesNum > 0 ? pluralize(valuesNum, 'Sales Agent', 'Sales Agents') : 'Sales Agents'
      case 'listingAgent':
        return valuesNum > 0 ? pluralize(valuesNum, 'Listing Agent', 'Listing Agents') : 'Listing Agents'
      case 'propertyOwner':
        return valuesNum > 0 ? pluralize(valuesNum, 'Property Owner', 'Property Owners') : 'Property Owners'
      default:
        return valuesNum > 0 ? pluralize(valuesNum, 'Agent', 'Agents') : 'Agents'
    }
  }

  const computeContextualVariables = useCallback(() => {
    let agentListName
    let inputId

    switch (matrixContext) {
      case 'listing':
        inputId = '_id_list'
        break
      case 'check_request':
        inputId = 'check_agent_values'
        break
      case 'lease':
        inputId = '_id_list'
      case 'listing_room':
        inputId = 'room_agent_values'
        break
    }
    switch (agentType) {
      case 'salesAgent':
        agentListName = 'sales_agent_id_list'
        inputId = `sales_agent${inputId}`
        break
      case 'listingAgent':
        agentListName = 'listing_agent_id_list'
        inputId = `listing_agent${inputId}`
        break
      case 'propertyOwner':
        agentListName = 'property_owner_id_list'
        inputId = `property_owner${inputId}`
        break
      case 'all':
        agentListName = 'agent_id_list'
        break
    }
    return { agentListName, inputId }
  }, [agentType, matrixContext])

  const { agentListName, inputId } = computeContextualVariables()
  const inputName = `${matrixContext}_search_form[${agentListName}]`

  return (
    <>
      <DropdownSelect
        className={s.root}
        inputPlaceholder={getPlaceholderText()}
        options={options.length > 0 ? options : []}
        values={selectedValues}
        onApply={handleApply}
        onClear={handleClear}
        selectedValuesFunc={getSelectedValuesText}
      />
      <input
        className={s.hiddenInput}
        type="hidden"
        value={selectedAgents.map((a) => a.id)}
        name={inputName}
        id={inputId}
      />
    </>
  )
}

SalesAgentComponent.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  selectedAgents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  initialSelectedAgents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  updateSelectedAgentsMultiple: PropTypes.func.isRequired,
  initializeAgents: PropTypes.func.isRequired,
  initializeSelectedAgents: PropTypes.func.isRequired,
  updateAgentMatches: PropTypes.func.isRequired,
  agentType: PropTypes.oneOf(['salesAgent', 'listingAgent', 'propertyOwner', 'all']).isRequired,
  matrixContext: PropTypes.oneOf(['listing', 'lease', 'check_request', 'listing_room', 'commission_check']).isRequired,
}

SalesAgentComponent.defaultProps = {
  agents: [],
  selectedAgents: [],
  initialSelectedAgents: [],
}

const mapStateToProps = (state, ownProps) => {
  const { agentType } = ownProps
  const agentState = state.agentTypeahead[agentType]

  return {
    agents: agentState.agents,
    selectedAgents: agentState.selectedAgents,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { agentType } = ownProps
  return {
    updateSelectedAgentsMultiple: (payload) => dispatch(updateSelectedAgentsMultiple(payload, agentType)),
    initializeAgents: (payload) => dispatch(initializeAgents(payload, agentType)),
    updateAgentMatches: () => dispatch(updateAgentMatches(agentType)),
    initializeSelectedAgents: (payload) => dispatch(initializeSelectedAgents(payload, agentType)),
  }
}

export const SalesAgent = connect(mapStateToProps, mapDispatchToProps)(AsyncSubscribe(['agents'])(SalesAgentComponent))
